import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { updateCurrentMember, getMemberUsage } from '../../../services/membersApi';
import { sendVerification } from '../../../services/emailApi';
import { MembershipBadge, MembershipLimits } from '../../common/MembershipBadge';
import { isTempEmail } from '../../../utils/email';
import './ProfileCard.css';

const SUCCESS_MESSAGE_TIMEOUT_MS = 3000;

interface ProfileCardProps {
  isMobile?: boolean;
  onNavigateToSettings?: () => void;
}

const ProfileCard = ({ isMobile = false, onNavigateToSettings }: ProfileCardProps) => {
  // Apply mobile-specific class if isMobile is true
  const containerClass = isMobile ? 'profile-card profile-card--mobile' : 'profile-card';
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    storeName: user?.storeName || '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');
  const [usageStats, setUsageStats] = useState({ currentAds: 0, currentContacts: 0 });
  const [shouldRefreshUsage, setShouldRefreshUsage] = useState(false);
  const saveSuccessRef = useRef(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  // Fetch usage stats - shared function
  const fetchUsageStats = useCallback(async () => {
    try {
      const usage = await getMemberUsage();
      if (usage) {
        setUsageStats({
          currentAds: usage.currentAds,
          currentContacts: usage.currentContacts,
        });
        return true;
      }
      return false;
    } catch {
      console.error('Failed to fetch usage stats');
      return false;
    }
  }, []);

  // Fetch usage stats when component mounts
  useEffect(() => {
    fetchUsageStats();
  }, [fetchUsageStats]);

  // Refresh usage stats after saving profile
  useEffect(() => {
    if (shouldRefreshUsage && saveSuccessRef.current) {
      fetchUsageStats();
      setShouldRefreshUsage(false);
      saveSuccessRef.current = false;
    }
  }, [shouldRefreshUsage, fetchUsageStats]);

  // Clear success message after timeout
  useEffect(() => {
    if (saveMessage) {
      const timer = setTimeout(() => setSaveMessage(''), SUCCESS_MESSAGE_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
  }, [saveMessage]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      name: user?.name || '',
      phone: user?.phone || '',
      storeName: user?.storeName || '',
    });
    setSaveMessage('');
    setError('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveMessage('');
    setError('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSaveMessage('');

    try {
      // Convert empty strings to null for API compatibility
      const apiData = {
        name: editData.name || undefined,
        phone: editData.phone || undefined,
        storeName: editData.storeName || undefined,
      };

      const updatedMember = await updateCurrentMember(apiData);

      if (updatedMember) {
        // Update auth context
        await updateUser(updatedMember);
        setSaveMessage('資料更新成功！');
        setIsEditing(false);

        // Trigger usage stats refresh
        saveSuccessRef.current = true;
        setShouldRefreshUsage(true);
      } else {
        setError('更新失敗，請稍後再試');
      }
    } catch {
      setError('更新失敗，請稍後再試');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBindEmail = () => {
    // Navigate to settings tab where EmailBindingForm is located
    if (onNavigateToSettings) {
      onNavigateToSettings();
    } else {
      navigate('/member?tab=settings');
    }
  };

  const handleSendVerification = async () => {
    setIsSendingVerification(true);
    setVerificationMessage('');
    setError('');

    try {
      const response = await sendVerification();
      if (response.success) {
        setVerificationMessage('驗證連結已發送到您的 Email！');
        setTimeout(() => setVerificationMessage(''), 5000);
      } else {
        setError(response.error || '發送失敗，請稍後再試');
      }
    } catch {
      setError('發送失敗，請稍後再試');
    } finally {
      setIsSendingVerification(false);
    }
  };



  if (!user) {
    return (
      <div className={containerClass}>
        <div className="profile-card__loading">載入中...</div>
      </div>
    );
  }

  // 安全地格式化日期
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '未知';
    const date = new Date(dateString);
    // 檢查日期是否有效
    if (isNaN(date.getTime())) return '未知';
    // 檢查是否為預設的 KV 初始化時間 (1970-01-01)
    if (date.getFullYear() === 1970) return '未知';
    return date.toLocaleDateString('zh-TW');
  };

  const userIsTempEmail = isTempEmail(user.email);

  return (
    <div className={containerClass}>
      <div className="profile-card__header">
        <h2 className="profile-card__title">會員資料</h2>
        {!isEditing && (
          <button onClick={handleEdit} className="profile-card__edit-btn">
            編輯資料
          </button>
        )}
      </div>

      {saveMessage && (
        <div className="profile-card__success">{saveMessage}</div>
      )}

      {error && (
        <div className="profile-card__error">{error}</div>
      )}

      <div className="profile-card__content">
        {isEditing ? (
          <div className="profile-card__form">
            <div className="profile-card__field">
              <label className="profile-card__label">姓名 *</label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="profile-card__input"
                disabled={isSaving}
              />
            </div>

            <div className="profile-card__field">
              <label className="profile-card__label">Email</label>
              <input
                type="email"
                value={user.email}
                className="profile-card__input profile-card__input--readonly"
                disabled
                title="Email 無法修改"
              />
              {userIsTempEmail && (
                <span className="profile-card__hint profile-card__hint--warning">
                  這是臨時 Email，請綁定真實 Email 以確保帳號安全
                </span>
              )}
              {!userIsTempEmail && (
                <span className="profile-card__hint">Email 無法修改</span>
              )}
            </div>

            <div className="profile-card__field">
              <label className="profile-card__label">電話</label>
              <input
                type="tel"
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                className="profile-card__input"
                placeholder="0912-345-678"
                disabled={isSaving}
              />
            </div>

            <div className="profile-card__field">
              <label className="profile-card__label">公司名稱</label>
              <input
                type="text"
                value={editData.storeName}
                onChange={(e) => setEditData({ ...editData, storeName: e.target.value })}
                className="profile-card__input"
                placeholder="請輸入公司名稱"
                disabled={isSaving}
              />
            </div>

            <div className="profile-card__actions">
              <button
                onClick={handleCancel}
                className="profile-card__cancel"
                disabled={isSaving}
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="profile-card__save"
                disabled={isSaving || !editData.name}
              >
                {isSaving ? '儲存中...' : '儲存變更'}
              </button>
            </div>
          </div>
        ) : (
          <div className="profile-card__info">
            <div className="profile-card__membership-section">
              <div className="profile-card__info-row profile-card__info-row--center">
                <span className="profile-card__info-label">會員等級：</span>
                <MembershipBadge
                  plan={user.plan || 'free'}
                  size="lg"
                />
              </div>
              <div className="profile-card__limits">
                <MembershipLimits
                  plan={user.plan || 'free'}
                  currentAds={usageStats.currentAds}
                  currentContacts={usageStats.currentContacts}
                />
              </div>
              {(user.plan === 'free' || !user.plan) && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="profile-card__upgrade-btn"
                >
                  ⭐ 升級會員
                </button>
              )}
            </div>

            <div className="profile-card__info-row">
              <span className="profile-card__info-label">姓名：</span>
              <span className="profile-card__info-value">{user.name}</span>
            </div>

            {userIsTempEmail ? (
              <div className="profile-card__info-row">
                <span className="profile-card__info-label">Email：</span>
                <span className="profile-card__info-value profile-card__info-value--warning">
                  &lt;未綁定&gt;
                </span>
                <button onClick={handleBindEmail} className="profile-card__bind-btn">
                  立即綁定
                </button>
              </div>
            ) : (
              <div className="profile-card__info-row profile-card__info-row--email">
                <div className="profile-card__email-section">
                  <div>
                    <span className="profile-card__info-label">Email：</span>
                    <span className="profile-card__info-value">{user.email}</span>
                  </div>
                  {/* v0.10.1: Email verification status */}
                  {user.emailVerified === false && (
                    <div className="profile-card__verification-status profile-card__verification-status--unverified">
                      <span className="profile-card__verification-badge">未驗證</span>
                      <button
                        onClick={handleSendVerification}
                        disabled={isSendingVerification}
                        className="profile-card__verify-btn"
                      >
                        {isSendingVerification ? '發送中...' : '發送驗證連結'}
                      </button>
                    </div>
                  )}
                  {user.emailVerified === true && (
                    <div className="profile-card__verification-status profile-card__verification-status--verified">
                      <span className="profile-card__verification-badge profile-card__verification-badge--verified">
                        ✓ 已驗證
                      </span>
                    </div>
                  )}
                  {verificationMessage && (
                    <div className="profile-card__verification-message">
                      {verificationMessage}
                    </div>
                  )}
                </div>
              </div>
            )}

            {user.phone && (
              <div className="profile-card__info-row">
                <span className="profile-card__info-label">電話：</span>
                <span className="profile-card__info-value">{user.phone}</span>
              </div>
            )}

            {user.storeName && (
              <div className="profile-card__info-row">
                <span className="profile-card__info-label">公司名稱：</span>
                <span className="profile-card__info-value">{user.storeName}</span>
              </div>
            )}

            <div className="profile-card__info-row">
              <span className="profile-card__info-label">加入日期：</span>
              <span className="profile-card__info-value">
                {formatDate(user.createdAt)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileCard;
